
import React, { useState } from 'react';
import { User, UserRole, Building } from '../types';
import { MOCK_BUILDINGS } from '../mockData';
import { Plus, MapPin, Building as BuildingIcon, User as UserIcon, Mail, Edit2, X, AlertTriangle } from 'lucide-react';

interface BuildingsProps {
  user: User;
}

const Buildings: React.FC<BuildingsProps> = ({ user }) => {
  const [buildings, setBuildings] = useState<Building[]>(MOCK_BUILDINGS);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [editingBuilding, setEditingBuilding] = useState<Building | null>(null);
  
  const [formState, setFormState] = useState<Partial<Building>>({
    name: '',
    address: '',
    apartmentCount: 0,
    managerName: '',
    managerEmail: ''
  });

  if (user.role !== UserRole.SUPER_ADMIN) return <div className="p-8 text-center text-slate-500">Пристап одбиен. Само Супер Администратор може да пристапи овде.</div>;

  const handleOpenForm = (building?: Building) => {
    if (building) {
      setEditingBuilding(building);
      setFormState(building);
    } else {
      setEditingBuilding(null);
      setFormState({ name: '', address: '', apartmentCount: 0, managerName: '', managerEmail: '' });
    }
    setIsFormOpen(true);
  };

  const handlePreSave = () => {
    setIsConfirmOpen(true);
  };

  const handleSave = () => {
    if (editingBuilding) {
      setBuildings(prev => prev.map(b => b.id === editingBuilding.id ? { ...formState, id: b.id } as Building : b));
    } else {
      const newBuilding: Building = {
        ...formState,
        id: `b-${Date.now()}`
      } as Building;
      setBuildings([newBuilding, ...buildings]);
    }
    setIsConfirmOpen(false);
    setIsFormOpen(false);
    setEditingBuilding(null);
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Управување со згради</h1>
          <p className="text-slate-500">Преглед и додавање на нови објекти во системот.</p>
        </div>
        <button 
          onClick={() => handleOpenForm()}
          className="bg-indigo-600 text-white px-4 py-2 rounded-xl font-semibold hover:bg-indigo-700 flex items-center transition-all shadow-lg shadow-indigo-200"
        >
          <Plus className="w-5 h-5 mr-2" /> Додај зграда
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {buildings.map(b => (
          <div key={b.id} className="bg-white p-6 rounded-2xl border border-slate-200 shadow-sm flex items-start space-x-4 relative group hover:border-indigo-300 transition-all">
            <div className="bg-indigo-50 p-4 rounded-xl">
              <BuildingIcon className="w-8 h-8 text-indigo-600" />
            </div>
            <div className="flex-1">
              <div className="flex justify-between items-start">
                <h3 className="text-xl font-bold text-slate-900">{b.name}</h3>
                <button 
                  onClick={() => handleOpenForm(b)}
                  className="p-2 text-slate-400 hover:text-indigo-600 opacity-0 group-hover:opacity-100 transition-all"
                >
                  <Edit2 className="w-4 h-4" />
                </button>
              </div>
              <p className="text-slate-500 flex items-center text-sm mt-1">
                <MapPin className="w-4 h-4 mr-1 text-slate-400" /> {b.address}
              </p>
              
              <div className="mt-4 grid grid-cols-2 gap-4">
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <span className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Управител</span>
                  <div className="flex items-center text-sm font-semibold text-slate-700">
                    <UserIcon className="w-3 h-3 mr-1 opacity-50" /> {b.managerName || 'Непознат'}
                  </div>
                </div>
                <div className="bg-slate-50 p-3 rounded-lg border border-slate-100">
                  <span className="block text-[10px] font-bold text-slate-400 uppercase mb-1">Контакт</span>
                  <div className="flex items-center text-sm font-semibold text-slate-700 truncate">
                    <Mail className="w-3 h-3 mr-1 opacity-50" /> {b.managerEmail || 'Нема меил'}
                  </div>
                </div>
              </div>

              <div className="mt-4 flex items-center justify-between border-t border-slate-50 pt-4">
                <span className="text-xs font-bold text-slate-400 uppercase">Број на станови</span>
                <span className="text-lg font-black text-slate-900">{b.apartmentCount}</span>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Form Sidebar */}
      {isFormOpen && (
        <div className="fixed inset-0 z-50 flex justify-end">
          <div className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" onClick={() => setIsFormOpen(false)} />
          <div className="relative w-full max-w-lg bg-white h-full shadow-2xl flex flex-col p-8 animate-in slide-in-from-right duration-300">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-2xl font-bold text-slate-900">{editingBuilding ? 'Уреди зграда' : 'Додај нова зграда'}</h2>
              <button onClick={() => setIsFormOpen(false)} className="p-2 hover:bg-slate-100 rounded-full">
                <X className="w-6 h-6" />
              </button>
            </div>

            <div className="flex-1 space-y-6 overflow-y-auto pr-2">
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-600">Име на зграда</label>
                <input 
                  type="text" 
                  value={formState.name}
                  onChange={(e) => setFormState({...formState, name: e.target.value})}
                  className="w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" 
                  placeholder="пр. Зграда Центар 1"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-600">Адреса</label>
                <input 
                  type="text" 
                  value={formState.address}
                  onChange={(e) => setFormState({...formState, address: e.target.value})}
                  className="w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" 
                  placeholder="пр. Ул. Македонија бр. 5"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-600">Број на станови</label>
                <input 
                  type="number" 
                  value={formState.apartmentCount}
                  onChange={(e) => setFormState({...formState, apartmentCount: parseInt(e.target.value)})}
                  className="w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" 
                />
              </div>
              <div className="space-y-2 pt-4 border-t">
                <label className="text-sm font-bold text-slate-600">Име на Управител</label>
                <input 
                  type="text" 
                  value={formState.managerName}
                  onChange={(e) => setFormState({...formState, managerName: e.target.value})}
                  className="w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" 
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-bold text-slate-600">Е-пошта на Управител</label>
                <input 
                  type="email" 
                  value={formState.managerEmail}
                  onChange={(e) => setFormState({...formState, managerEmail: e.target.value})}
                  className="w-full p-3 bg-slate-50 border rounded-xl focus:ring-2 focus:ring-indigo-500 outline-none" 
                />
              </div>
            </div>

            <div className="pt-8 border-t mt-auto">
              <button 
                onClick={handlePreSave}
                className="w-full py-4 bg-indigo-600 text-white rounded-xl font-bold shadow-lg shadow-indigo-100 hover:bg-indigo-700 transition-colors"
              >
                Зачувај промени
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirmation Dialog */}
      {isConfirmOpen && (
        <div className="fixed inset-0 z-[60] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" />
          <div className="relative bg-white p-8 rounded-3xl shadow-2xl max-w-sm w-full animate-in zoom-in duration-200">
            <div className="w-16 h-16 bg-amber-50 rounded-full flex items-center justify-center mx-auto mb-6 text-amber-600">
              <AlertTriangle className="w-8 h-8" />
            </div>
            <h3 className="text-xl font-bold text-center text-slate-900 mb-2">Потврдете ја акцијата</h3>
            <p className="text-slate-500 text-center text-sm mb-8">
              Дали сте сигурни дека сакате да ги зачувате информациите за оваа зграда?
            </p>
            <div className="grid grid-cols-2 gap-4">
              <button 
                onClick={() => setIsConfirmOpen(false)}
                className="py-3 px-4 rounded-xl font-bold text-slate-500 bg-slate-100 hover:bg-slate-200"
              >
                Откажи
              </button>
              <button 
                onClick={handleSave}
                className="py-3 px-4 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-100"
              >
                Потврди
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Buildings;
